import { useEffect, useMemo } from 'react';
import { useTranslation } from 'react-i18next';
import { useQuery } from 'react-query';
import dayjs from 'dayjs';
import styled from 'styled-components';

import { DATE_FORMAT, SUPPORTED_DATE_FORMATS } from '@/constants/common';
import {
  getInitiativeChildren,
  getInitiativeParents,
} from '@/features/canvas/api/initiative-relationships';
import { InitiativeDetailField } from '@/features/canvas/components/InitiativeDetailField';
import { InitiativeDetailFilterTag } from '@/features/canvas/components/InitiativeDetailFilterTag';
import { CHARACTER_LIMITS } from '@/features/canvas/constants/character-limits';
import {
  InitiativeFieldTitles,
  InitiativeFieldTypes,
} from '@/features/canvas/constants/initiative-field';
import { useEditorContext } from '@/features/canvas/contexts/editor-context';
import { renderTextWithHyperlinks } from '@/features/canvas/utils/render-text-with-hyperlinks';
import { ProgressBar } from '@/features/ui/components/ProgressBar';
import { components } from '@/types/api';

interface InitiativeGeneralInfoTabProps {
  initiative?: components['schemas']['Initiative'] | null;
  onRelationshipsStatusChange?: (hasRelationships: boolean) => void;
}

export const InitiativeGeneralInfoTab = ({
  initiative,
  onRelationshipsStatusChange,
}: InitiativeGeneralInfoTabProps) => {
  const {
    selectedNodeId,
    filters,
    popupVisible,
    setSelectedNodeId,
    setSelectedInitiativeId,
    filteredNodes,
    roadmapNodes,
  } = useEditorContext();
  const { t } = useTranslation();

  const fields = useMemo(() => {
    if (!initiative?.fields) return [];

    const defaultStart = {
      bigFieldsCounter: 0,
      smallFieldsCounter: 0,
      isStartDatePresent: false,
      isEndDatePresent: false,
      orderMapToId: new Map<number, number>(),
      fields: [] as components['schemas']['InitiativeField'][],
    };

    const dialogLayoutCreator = initiative.fields.reduce((acc, currentField) => {
      const accClone = { ...acc };

      const isVisible = popupVisible.find(
        f => f.title === currentField.title && f.type === currentField.type
      );
      if (!isVisible) {
        return accClone;
      }

      // Skip budget fields for general info tab
      if (
        currentField.type === InitiativeFieldTypes.budget ||
        ['capex', 'opex', 'spend', 'total'].includes(currentField.type || '')
      ) {
        return accClone;
      }

      switch (currentField.type) {
        case InitiativeFieldTypes.description:
          accClone.bigFieldsCounter += 1;
          accClone.orderMapToId.set(Number(currentField.id), 1);
          accClone.fields.push(currentField);
          break;

        case InitiativeFieldTypes.date:
          if (currentField.title === InitiativeFieldTitles.startDate) {
            accClone.smallFieldsCounter += 1;
            accClone.isStartDatePresent = true;
            accClone.orderMapToId.set(Number(currentField.id), 2);
          }
          if (currentField.title === InitiativeFieldTitles.endDate) {
            accClone.smallFieldsCounter += 1;
            accClone.isEndDatePresent = true;
            accClone.orderMapToId.set(Number(currentField.id), 3);
          }
          accClone.fields.push(currentField);
          break;
        case InitiativeFieldTypes.small:
          accClone.smallFieldsCounter += 1;
          accClone.orderMapToId.set(Number(currentField.id), 5);
          accClone.fields.push(currentField);
          break;
        case InitiativeFieldTypes.big:
          accClone.bigFieldsCounter += 1;
          accClone.orderMapToId.set(Number(currentField.id), 6);
          accClone.fields.push(currentField);
          break;
        case InitiativeFieldTypes.progressBar:
          accClone.orderMapToId.set(Number(currentField.id), 7);
          accClone.fields.push(currentField);
          break;
        case InitiativeFieldTypes.title:
          break;
        default: {
          accClone.smallFieldsCounter += 1;
          accClone.fields.push(currentField);
          break;
        }
      }
      return accClone;
    }, defaultStart);

    // Handle layout adjustments based on date fields presence
    if (dialogLayoutCreator.isStartDatePresent && dialogLayoutCreator.isEndDatePresent) {
      let maxSmallFieldsThatFitsSpace = 2;
      dialogLayoutCreator.orderMapToId.forEach((value, key) => {
        if (value === 6 && maxSmallFieldsThatFitsSpace !== 0) {
          dialogLayoutCreator.orderMapToId.set(key, 4);
          maxSmallFieldsThatFitsSpace -= 1;
        }
      });
    }

    if (
      (dialogLayoutCreator.isStartDatePresent && !dialogLayoutCreator.isEndDatePresent) ||
      (!dialogLayoutCreator.isStartDatePresent && dialogLayoutCreator.isEndDatePresent)
    ) {
      let maxSmallFieldsThatFitsSpace = 3;
      dialogLayoutCreator.orderMapToId.forEach((value, key) => {
        if (value === 6 && maxSmallFieldsThatFitsSpace !== 0) {
          dialogLayoutCreator.orderMapToId.set(key, 4);
          maxSmallFieldsThatFitsSpace -= 1;
        }
      });
    }

    if (!dialogLayoutCreator.isStartDatePresent && !dialogLayoutCreator.isEndDatePresent) {
      let maxBigFieldsInLayout = 4;
      dialogLayoutCreator.orderMapToId.forEach((value, key) => {
        if (value === 5 && maxBigFieldsInLayout !== 0) {
          dialogLayoutCreator.orderMapToId.set(key, 4);
          maxBigFieldsInLayout -= 1;
        }
      });
    }

    // Sort fields based on order
    dialogLayoutCreator.fields.sort(({ id: idA }, { id: idB }) => {
      const orderA = dialogLayoutCreator.orderMapToId.get(Number(idA));
      const orderB = dialogLayoutCreator.orderMapToId.get(Number(idB));

      if (orderA && orderB) {
        return orderA - orderB;
      }
      return 0;
    });

    // Return all fields without restriction
    return dialogLayoutCreator.fields;
  }, [initiative?.fields, popupVisible]);

  // Fetch parents
  const { data: parents, isLoading: isLoadingParents } = useQuery(
    ['getInitiativeParents', initiative?.id],
    () => getInitiativeParents({ initiativeId: initiative!.id! }),
    {
      enabled: !!initiative?.id,
      staleTime: 0,
      refetchOnMount: 'always',
    }
  );

  // Fetch children
  const { data: children, isLoading: isLoadingChildren } = useQuery(
    ['getInitiativeChildren', initiative?.id],
    () => getInitiativeChildren({ initiativeId: initiative!.id! }),
    {
      enabled: !!initiative?.id,
      staleTime: 0,
      refetchOnMount: 'always',
    }
  );

  // Format parent/child names for display (no longer used for comma-separated display)
  const hasParents = Boolean(parents && parents.length > 0);
  const hasChildren = Boolean(children && children.length > 0);

  // Notify parent component about relationships status for layout adjustments
  useEffect(() => {
    onRelationshipsStatusChange?.(hasParents || hasChildren);
  }, [hasParents, hasChildren, onRelationshipsStatusChange]);

  // Function to handle clicking on a parent/child initiative name
  const handleInitiativeClick = (initiativeId: number) => {
    // First try filteredNodes
    const targetNode = filteredNodes.find(node => node.initiatives?.id === initiativeId);

    // If not found, try roadmapNodes
    if (!targetNode) {
      const roadmapNode = roadmapNodes.find(node => node.initiatives?.id === initiativeId);
      if (roadmapNode) {
        // Clear selectedInitiativeId when we have a node
        setSelectedInitiativeId(null);
        setSelectedNodeId(Number(roadmapNode.id));
        return;
      }
    }

    if (targetNode) {
      // Clear selectedInitiativeId when we have a node
      setSelectedInitiativeId(null);
      setSelectedNodeId(Number(targetNode.id));
      return;
    }

    // If initiative not found in either node collection, use initiative ID directly
    // This enables cross-roadmap navigation
    setSelectedNodeId(null);
    setSelectedInitiativeId(initiativeId);
  };

  const filtersToDisplay = useMemo(() => {
    return filters.filter(f => {
      if (!f.isVisible) {
        return false;
      }
      const selectedNodeFilterOptions = f?.nodes?.filter(
        node => Number(node.nodeId) === Number(selectedNodeId)
      );

      if (selectedNodeFilterOptions?.length) {
        const filteredOptions = f.options.filter(o =>
          selectedNodeFilterOptions?.find(
            nodeFilterOption => Number(nodeFilterOption.optionId) === Number(o.id)
          )
        );
        return filteredOptions.length;
      }
      return false;
    });
  }, [filters, selectedNodeId]);

  const isDateIncluded = fields.find(f => f.type === InitiativeFieldTypes.date);
  const totalColumns = hasParents || hasChildren ? 5 : 4;

  const getInitiativeFieldComponent = (field: components['schemas']['InitiativeField']) => {
    switch (field.type) {
      case InitiativeFieldTypes.description:
        return (
          <InitiativeDetailField
            key={field.id}
            colSize={2}
            rowSize={2}
            title={field.title}
            content={
              <StyledLargeText>
                {renderTextWithHyperlinks(field.value ?? '', field.hyperlinks)}
              </StyledLargeText>
            }
          />
        );
      case InitiativeFieldTypes.date:
        return (
          <InitiativeDetailField
            key={field.id}
            title={field.title}
            value={
              field.value
                ? dayjs(field.value, SUPPORTED_DATE_FORMATS).format(DATE_FORMAT)
                : t('not_specified')
            }
          />
        );
      case InitiativeFieldTypes.progressBar:
        return (
          <InitiativeDetailField
            colSize={totalColumns}
            key={field.id}
            title={field.title}
            content={
              <ProgressBarWrapper>
                <ProgressBar value={field.value || '0'} />
              </ProgressBarWrapper>
            }
          />
        );
      case InitiativeFieldTypes.big: {
        const bigFields = fields.filter(f => f.type === InitiativeFieldTypes.big);
        const currentBigFieldIndex = bigFields.findIndex(f => f.id === field.id);

        return (
          <InitiativeDetailField
            colSize={2}
            rowSize={2}
            {...(isDateIncluded && {
              colEnd: currentBigFieldIndex % 2 === 0 ? 3 : 5,
            })}
            key={field.id}
            title={field.title}
            titleDisplayLimit={CHARACTER_LIMITS.CUSTOM_FIELD_TITLE_DISPLAY}
            content={
              <StyledLargeText>
                {renderTextWithHyperlinks(field.value ?? '', field.hyperlinks)}
              </StyledLargeText>
            }
          />
        );
      }
      case InitiativeFieldTypes.small:
        return (
          <InitiativeDetailField
            key={field.id}
            title={field.title}
            titleDisplayLimit={CHARACTER_LIMITS.TITLE_DISPLAY}
            content={
              field.hyperlinks?.[0]?.url ? (
                <StyledAnchor
                  href={field.hyperlinks[0].url}
                  target="_blank"
                  rel="noopener noreferrer"
                >
                  {field.value}
                </StyledAnchor>
              ) : (
                <StyledText>{field.value}</StyledText>
              )
            }
          />
        );
      case InitiativeFieldTypes.placeholder:
        return null;
      default:
        return <InitiativeDetailField key={field.id} title={field.title} value={field.value} />;
    }
  };

  // Render fields in specific order to match Figma layout
  const renderFieldsInOrder = () => {
    const orderedComponents = [];

    // Get specific field types
    const descriptionField = fields.find(f => f.type === InitiativeFieldTypes.description);
    const startDateField = fields.find(
      f => f.type === InitiativeFieldTypes.date && f.title === InitiativeFieldTitles.startDate
    );
    const endDateField = fields.find(
      f => f.type === InitiativeFieldTypes.date && f.title === InitiativeFieldTitles.endDate
    );
    const progressField = fields.find(f => f.type === InitiativeFieldTypes.progressBar);

    // Find specific other fields we need to position
    const budgetField = fields.find(f => f.title === 'Budget' || f.title === 'Total Budget');
    const projectOwnerField = fields.find(f => f.title === 'Project Owner');

    // Get remaining other fields (excluding the ones we're positioning specifically)
    const otherFields = fields.filter(
      f =>
        f.type !== InitiativeFieldTypes.description &&
        f.type !== InitiativeFieldTypes.progressBar &&
        !(
          f.type === InitiativeFieldTypes.date &&
          (f.title === InitiativeFieldTitles.startDate || f.title === InitiativeFieldTitles.endDate)
        ) &&
        f !== budgetField &&
        f !== projectOwnerField
    );

    // Grid layout (4 columns):
    // Description (2x2) | Start Date | End Date | Parent Initiatives
    // (Description cont.) | Project Owner | Budget | Child Initiatives
    // Progress (4x1)
    // Other fields...

    // Add description first (it spans 2x2)
    if (descriptionField) {
      orderedComponents.push(getInitiativeFieldComponent(descriptionField));
    }

    // ROW 1 (right side): Start Date | End Date | Combined Parent/Child field
    if (startDateField) {
      orderedComponents.push(getInitiativeFieldComponent(startDateField));
    }
    if (endDateField) {
      orderedComponents.push(getInitiativeFieldComponent(endDateField));
    }

    // Conditionally add Parent/Child Initiatives field based on what relationships exist
    if (hasParents || hasChildren) {
      const parentInitiativesLabel = `${t('editor.sidebar.relationships.parent_initiatives')}:`;
      const childInitiativesLabel = `${t('editor.sidebar.relationships.child_initiatives')}:`;

      orderedComponents.push(
        <InitiativeDetailField
          key="parent-child-initiatives"
          colSize={1}
          rowSize={2}
          content={
            <StyledRelationshipsContent>
              {hasParents && (
                <RelationshipSection>
                  <RelationshipLabel>{parentInitiativesLabel}</RelationshipLabel>
                  <RelationshipValue>
                    {(() => {
                      if (isLoadingParents) {
                        return t('loading');
                      }
                      return (
                        <InitiativesList>
                          {parents?.map(parent => (
                            <InitiativeItem
                              key={parent.id}
                              onClick={() => parent.id && handleInitiativeClick(parent.id)}
                            >
                              {parent.title}
                            </InitiativeItem>
                          ))}
                        </InitiativesList>
                      );
                    })()}
                  </RelationshipValue>
                </RelationshipSection>
              )}

              {hasChildren && (
                <RelationshipSection>
                  <RelationshipLabel>{childInitiativesLabel}</RelationshipLabel>
                  <RelationshipValue>
                    {(() => {
                      if (isLoadingChildren) {
                        return t('loading');
                      }
                      return (
                        <InitiativesList>
                          {children?.map(child => (
                            <InitiativeItem
                              key={child.id}
                              onClick={() => child.id && handleInitiativeClick(child.id)}
                            >
                              {child.title}
                            </InitiativeItem>
                          ))}
                        </InitiativesList>
                      );
                    })()}
                  </RelationshipValue>
                </RelationshipSection>
              )}
            </StyledRelationshipsContent>
          }
        />
      );
    }

    // ROW 2 (right side): Project Owner | Budget | (space taken by combined field)
    if (projectOwnerField) {
      orderedComponents.push(getInitiativeFieldComponent(projectOwnerField));
    }
    if (budgetField) {
      orderedComponents.push(getInitiativeFieldComponent(budgetField));
    }

    // Add remaining other fields
    otherFields.forEach(field => {
      orderedComponents.push(getInitiativeFieldComponent(field));
    });

    // Add progress bar last (full width)
    if (progressField) {
      orderedComponents.push(getInitiativeFieldComponent(progressField));
    }

    return orderedComponents;
  };

  return (
    <TabContent>
      <InitiativeDetailsGrid $hasRelationships={hasParents || hasChildren}>
        {renderFieldsInOrder()}
      </InitiativeDetailsGrid>
      {Boolean(filtersToDisplay.length) && initiative && (
        <InitiativeDetailField
          colSize={totalColumns}
          title={t('editor.initiative_detail.filters')}
          key="filter-field"
          content={
            <FiltersWrapper>
              {filtersToDisplay.map(filter => {
                const selectedNodeFilterOptions = filter?.nodes?.filter(
                  node => Number(node.nodeId) === Number(selectedNodeId)
                );
                const filteredOptions = filter.options.filter(o =>
                  selectedNodeFilterOptions?.find(
                    nodeFilterOption => nodeFilterOption.optionId === o.id
                  )
                );

                return (
                  <InitiativeDetailFilterTag
                    key={filter.id}
                    title={`${filter.title}:`}
                    options={filteredOptions}
                    filterShape={filter.shape}
                    filterSelection={filter.selection}
                  />
                );
              })}
            </FiltersWrapper>
          }
        />
      )}
    </TabContent>
  );
};

const TabContent = styled.div`
  padding: 2rem;
  height: 100%;
  overflow-y: auto;
`;

const InitiativeDetailsGrid = styled.div<{ $hasRelationships: boolean }>`
  display: grid;
  gap: 2rem;
  grid-template-columns: repeat(${({ $hasRelationships }) => ($hasRelationships ? 5 : 4)}, 1fr);
  grid-auto-rows: min(50px 80px);
  margin-bottom: 2rem;

  & [class*='MuiTypography-root']:first-child,
  & > div > div:first-child {
    font-size: 1.4rem !important;
    font-weight: 600 !important;
    color: ${({ theme }) => theme.palette.brand.textPrimary} !important;
  }
`;

const StyledText = styled.span`
  color: #6b6b6b;
  letter-spacing: 0.1px;
  line-height: 1.5;
  font-size: 1.4rem;
  word-break: break-word;
  overflow: hidden;
  width: 100%;
`;

const StyledLargeText = styled.span`
  color: #6b6b6b;
  letter-spacing: 0.1px;
  line-height: 1.5;
  font-size: 1.4rem;
  word-break: break-word;
  width: 100%;
  display: block;
  max-height: 120px;
  overflow-y: auto;

  /* Custom scrollbar styling */
  &::-webkit-scrollbar {
    width: 6px;
  }

  &::-webkit-scrollbar-track {
    background: ${({ theme }) => theme.palette.brand.backgroundDialog};
    border-radius: 3px;
  }

  &::-webkit-scrollbar-thumb {
    background: ${({ theme }) => theme.palette.brand.textSecondary};
    border-radius: 3px;

    &:hover {
      background: ${({ theme }) => theme.palette.brand.textPrimary};
    }
  }
`;

const StyledAnchor = styled.a`
  color: #169fe8;
  letter-spacing: 0.1px;
  line-height: 1.5;
  font-size: 1.4rem;
  padding: 0;
  word-break: break-word;
  overflow: hidden;
  width: 100%;
  text-decoration: underline;
`;

const ProgressBarWrapper = styled.div`
  width: 100%;
`;

const FiltersWrapper = styled.div`
  display: flex;
  align-items: center;
  gap: 1rem;
  flex-wrap: wrap;
`;

const StyledRelationshipsContent = styled.div`
  display: flex;
  flex-direction: column;
  gap: 1.2rem;
  width: 100%;
  max-height: 120px;
  overflow-y: auto;

  /* Custom scrollbar styling */
  &::-webkit-scrollbar {
    width: 6px;
  }

  &::-webkit-scrollbar-track {
    background: ${({ theme }) => theme.palette.brand.backgroundDialog};
    border-radius: 3px;
  }

  &::-webkit-scrollbar-thumb {
    background: ${({ theme }) => theme.palette.brand.textSecondary};
    border-radius: 3px;

    &:hover {
      background: ${({ theme }) => theme.palette.brand.textPrimary};
    }
  }
`;

const RelationshipSection = styled.div`
  display: flex;
  flex-direction: column;
  gap: 0.4rem;
`;

const RelationshipLabel = styled.div`
  font-size: 1.4rem;
  font-weight: 600;
  color: ${({ theme }) => theme.palette.brand.textPrimary};
`;

const RelationshipValue = styled.div`
  font-size: 1.4rem;
  color: #6b6b6b;
  letter-spacing: 0.1px;
  line-height: 1.5;
  word-break: break-word;
`;

const InitiativesList = styled.div`
  display: flex;
  flex-direction: column;
  gap: 0.2rem;
`;

const InitiativeItem = styled.div`
  padding-left: 0.4rem;
  font-size: 1.4rem;
  color: #6b6b6b;
  letter-spacing: 0.1px;
  line-height: 1.5;
  word-break: break-word;
  cursor: pointer;

  &:hover {
    color: #2cbef4;
    text-decoration: underline;
  }
`;
